import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type KeyboardCodes,
  type KeyboardSensorOptions,
  type MouseSensorOptions,
  type TouchSensorOptions,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { AlertTriangle, Keyboard, Kanban, RotateCcw, Search } from 'lucide-react';
import type { CrmDealListItem } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useSheetParam } from '@/hooks/useSheetParam';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import type { AppShellContext } from '@/components/layout/AppShell';
import { CloseDealDialog } from './deals/CloseDealDialog';
import { DealCardBody } from './deals/DealCard';
import { DealColumn, MoneySummary, type ColumnKind } from './deals/DealColumn';
import { DealDetailSheet } from './deals/DealDetailSheet';
import {
  SCREEN_READER_INSTRUCTIONS,
  findCard,
  hasOpenRadixLayer,
  isTypingTarget,
  makeAnnouncements,
  makeCoordinateGetter,
  stageShortcutTarget,
} from './deals/boardKeyboard';
import { useDealBoard } from './deals/useDealBoard';

const INACTIVE_COLUMN_ID = '__inactive__';

// Sensor options live at module scope: `useSensor` memoises on the options
// object, so an inline literal would hand DndContext a new sensors array on
// every render and re-bind every card's activator listeners.
// Mouse + Touch instead of Pointer: a single PointerSensor cannot give the
// mouse a distance threshold AND touch a long-press delay at the same time
// (a 6 px mouse threshold on touch fights the column scroll).
const MOUSE_SENSOR_OPTIONS: MouseSensorOptions = { activationConstraint: { distance: 6 } };
const TOUCH_SENSOR_OPTIONS: TouchSensorOptions = { activationConstraint: { delay: 250, tolerance: 8 } };
// Enter stays "open the deal"; only Space lifts and drops.
const KEYBOARD_CODES: KeyboardCodes = { start: ['Space'], cancel: ['Escape'], end: ['Space'] };

/** Focus a card by deal id — the node remounts when the card changes column, so wait a frame. */
function refocusCard(dealId: string) {
  requestAnimationFrame(() => findCard(dealId)?.focus());
}

const SHORTCUTS: ReadonlyArray<{ keys: string; labelKey: string }> = [
  { keys: 'Enter', labelKey: 'open' },
  { keys: 'Space', labelKey: 'lift' },
  { keys: '← →', labelKey: 'move' },
  { keys: 'Space / Esc', labelKey: 'drop' },
  { keys: ']', labelKey: 'nextStage' },
  { keys: '[', labelKey: 'prevStage' },
  { keys: 'W', labelKey: 'won' },
  { keys: 'L', labelKey: 'lost' },
  { keys: '/', labelKey: 'search' },
];

interface ColumnSpec {
  id: string;
  title: string;
  kind: ColumnKind;
}

/**
 * The pipeline board: one droppable column per open stage, collapsed
 * won/lost rails that widen during a drag, a right-hand deal sheet and the
 * `?deal=` deep link. Every stage change — drag, shortcut, stepper, undo —
 * goes through `useDealBoard.moveDeal`.
 */
export function DealList() {
  const { nip } = useOutletContext<AppShellContext>();
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const currentUserId = userInfo?.id ?? null;
  const board = useDealBoard(nip, currentUserId);
  const { stages, stageIndex, deals, visibleDeals, loading, forbidden, loadError, pending, now, load, moveDeal } =
    board;
  const stagesById = stageIndex.byId;
  const { search, setSearch, mine, setMine, overdueOnly, setOverdueOnly, showClosed, setShowClosed } =
    board.filters;

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  // Deep link `?deal=<id>` (AccountDetail rows, "Kopiuj link"). `openDeal` is
  // referentially stable, so it can go straight to the memoised cards.
  const { id: openDealId, open: openDeal, close: closeDeal } = useSheetParam('deal');
  const searchRef = useRef<HTMLInputElement>(null);

  const navigateAway = useCallback(
    (to: string) => {
      closeDeal();
      navigate(to);
    },
    [closeDeal, navigate],
  );

  // Column order: open stages by sortOrder, then the won and lost rails,
  // then (only when non-empty) a display-only column for inactive stages.
  const dealsByColumn = useMemo(() => {
    const map = new Map<string, CrmDealListItem[]>();
    stages.forEach((stage) => map.set(stage.id, []));
    map.set(INACTIVE_COLUMN_ID, []);
    for (const deal of visibleDeals) {
      const stage = stagesById.get(deal.stageId);
      const bucket = stage && (stage.active || stage.isWon || stage.isLost) ? map.get(stage.id) : map.get(INACTIVE_COLUMN_ID);
      bucket?.push(deal);
    }
    return map;
  }, [stages, stagesById, visibleDeals]);

  // Which columns have deals hidden purely by the filters ("Nic nie pasuje").
  const rawCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const deal of deals) counts.set(deal.stageId, (counts.get(deal.stageId) ?? 0) + 1);
    return counts;
  }, [deals]);

  const columns = useMemo<ColumnSpec[]>(() => {
    const { open, won, lost } = stageIndex;
    const list: ColumnSpec[] = open.map((stage) => ({ id: stage.id, title: stage.name, kind: 'open' }));
    if (won) list.push({ id: won.id, title: won.name, kind: 'won' });
    if (lost) list.push({ id: lost.id, title: lost.name, kind: 'lost' });
    if ((dealsByColumn.get(INACTIVE_COLUMN_ID)?.length ?? 0) > 0) {
      list.push({ id: INACTIVE_COLUMN_ID, title: t('crm.deals.board.inactiveStage'), kind: 'inactive' });
    }
    return list;
  }, [stageIndex, dealsByColumn]);

  // Derived from the stages alone (never from deals or filters) so the
  // keyboard sensor — and with it every card's activator listeners — stays
  // stable across search keystrokes.
  const droppableIds = useMemo(() => {
    const { open, won, lost } = stageIndex;
    return [...open, won, lost].flatMap((stage) => (stage ? [stage.id] : []));
  }, [stageIndex]);

  const coordinateGetter = useMemo(() => makeCoordinateGetter(droppableIds), [droppableIds]);
  const keyboardSensorOptions = useMemo<KeyboardSensorOptions>(
    () => ({ coordinateGetter, keyboardCodes: KEYBOARD_CODES }),
    [coordinateGetter],
  );
  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor, TOUCH_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, keyboardSensorOptions),
  );

  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const hit = pointerWithin(args);
    return hit.length ? hit : rectIntersection(args);
  }, []);

  const accessibility = useMemo(
    () => ({
      announcements: makeAnnouncements({
        dealName: (id) => deals.find((deal) => deal.id === id)?.name ?? '',
        stageName: (id) => (id === undefined ? '' : (stagesById.get(String(id))?.name ?? '')),
      }),
      screenReaderInstructions: SCREEN_READER_INSTRUCTIONS,
    }),
    [deals, stagesById],
  );

  const endDrag = useCallback(() => {
    document.body.classList.remove('select-none', 'touch-none');
    setActiveId(null);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    document.body.classList.add('select-none', 'touch-none');
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      endDrag();
      if (event.over) void moveDeal(String(event.active.id), String(event.over.id));
    },
    [endDrag, moveDeal],
  );

  // Won/lost shortcuts open the confirmation dialog; remember which card asked
  // so focus can return to it after the confirm (a drag-initiated close has
  // no keyboard focus to restore).
  const shortcutCloseIdRef = useRef<string | null>(null);
  const closeRequestDealId = board.closeRequest?.dealId ?? null;
  const { confirmClose, cancelClose } = board;
  const handleConfirmClose = useCallback(
    async (input: { lostReason?: string; note?: string }) => {
      await confirmClose(input);
      if (closeRequestDealId && shortcutCloseIdRef.current === closeRequestDealId) {
        shortcutCloseIdRef.current = null;
        refocusCard(closeRequestDealId);
      }
    },
    [confirmClose, closeRequestDealId],
  );
  const handleCancelClose = useCallback(() => {
    shortcutCloseIdRef.current = null;
    cancelClose();
  }, [cancelClose]);

  // Letter shortcuts on a focused card + `/` for the search box. Never while
  // typing and never while any dialog/sheet owns the keyboard.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event) || hasOpenRadixLayer()) return;
      if (event.key === '/') {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      const card = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('[data-deal-id]');
      const dealId = card?.dataset.dealId;
      if (!dealId) return;
      const deal = deals.find((item) => item.id === dealId);
      const target = deal && stageShortcutTarget(event.key, deal, stageIndex);
      if (!target) return;
      event.preventDefault();
      // Won/lost open the confirmation — focus returns after it closes. An
      // open-stage move remounts the card in another column, which drops
      // focus — put it back so `]` `]` `]` walks the pipeline.
      if (target.close) shortcutCloseIdRef.current = dealId;
      void moveDeal(dealId, target.stageId);
      if (!target.close) refocusCard(dealId);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [deals, stageIndex, moveDeal]);

  const activeDeal = activeId ? deals.find((deal) => deal.id === activeId) : undefined;
  const openDeals = useMemo(() => deals.filter((deal) => deal.closedAt === null), [deals]);
  const openDealItem = openDealId ? deals.find((deal) => deal.id === openDealId) : undefined;
  const closingDeal = board.closeRequest ? deals.find((deal) => deal.id === board.closeRequest?.dealId) : undefined;

  if (forbidden) {
    return (
      <div className="flex h-full items-center justify-center py-16">
        <p className="text-muted-foreground text-sm">{t('errors.accessDenied')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-64" />
          <Skeleton className="ml-auto h-9 w-56" />
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="flex min-w-max gap-3 overflow-x-auto pb-4">
          {Array.from({ length: 5 }).map((_, column) => (
            <div key={column} className="w-72 shrink-0 space-y-2 rounded-lg border p-2">
              <Skeleton className="h-5 w-32" />
              {Array.from({ length: 3 }).map((__, row) => (
                <Skeleton key={row} className="h-[84px] w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const boardEmpty = deals.length === 0;

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex h-10 flex-wrap items-center gap-2">
        <p className="text-sm whitespace-nowrap">
          <span className="font-medium tabular-nums">{t('crm.deals.board.summary', { count: openDeals.length })}</span>
          <span className="text-muted-foreground"> · </span>
          <MoneySummary deals={openDeals} weighted className="font-medium tabular-nums" />
        </p>
        <div className="relative ml-auto w-55">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('crm.deals.board.searchPlaceholder')}
            aria-label={t('crm.deals.board.searchPlaceholder')}
            className="h-9 pl-8"
          />
        </div>
        <Toggle variant="outline" size="sm" pressed={mine} onPressedChange={setMine} className="px-3">
          {t('crm.deals.board.mine')}
        </Toggle>
        <Toggle variant="outline" size="sm" pressed={overdueOnly} onPressedChange={setOverdueOnly} className="px-3">
          {t('crm.deals.board.overdue')}
        </Toggle>
        <Toggle variant="outline" size="sm" pressed={showClosed} onPressedChange={setShowClosed} className="px-3">
          {t('crm.deals.board.showClosed')}
        </Toggle>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t('crm.deals.board.refresh')} onClick={() => void load()}>
              <RotateCcw className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('crm.deals.board.refresh')}</TooltipContent>
        </Tooltip>
        <Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t('crm.deals.board.shortcuts.title')}>
                  <Keyboard className="size-4" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>{t('crm.deals.board.shortcuts.title')}</TooltipContent>
          </Tooltip>
          <PopoverContent align="end" className="w-64 p-3">
            <p className="mb-2 text-sm font-medium">{t('crm.deals.board.shortcuts.title')}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
              {SHORTCUTS.map((shortcut) => (
                <div key={shortcut.labelKey} className="contents">
                  <dt>
                    <kbd className="bg-muted rounded border px-1.5 py-0.5 font-mono text-[11px]">{shortcut.keys}</kbd>
                  </dt>
                  <dd className="text-muted-foreground self-center">
                    {t(`crm.deals.board.shortcuts.${shortcut.labelKey}`)}
                  </dd>
                </div>
              ))}
            </dl>
          </PopoverContent>
        </Popover>
      </div>

      {loadError && boardEmpty ? (
        // A failed load is never "your pipeline is empty" — that copy would
        // send the user off to convert leads. (A failed REFRESH with deals
        // already on screen keeps the board; the hook toasts the error.)
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center">
          <AlertTriangle className="text-destructive size-10" />
          <p className="font-medium">{t('errors.loadingFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            <RotateCcw className="size-3.5" />
            {t('crm.deals.board.retry')}
          </Button>
        </div>
      ) : boardEmpty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center">
          <Kanban className="text-muted-foreground size-10" />
          <p className="font-medium">{t('crm.deals.board.emptyTitle')}</p>
          <p className="text-muted-foreground text-sm">{t('crm.deals.board.emptyHint')}</p>
          <Button asChild variant="link" size="sm">
            <Link to={`/${nip}/crm`}>{t('crm.deals.board.goToLeads')}</Link>
          </Button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          accessibility={accessibility}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={endDrag}
        >
          <div className={cn('flex min-w-max gap-3 overflow-x-auto pb-4', activeId && 'cursor-grabbing')}>
            {columns.map((column) => {
              const items = dealsByColumn.get(column.id) ?? [];
              return (
                <DealColumn
                  key={column.id}
                  id={column.id}
                  title={column.title}
                  kind={column.kind}
                  stagesById={stagesById}
                  deals={items}
                  now={now}
                  pending={pending}
                  dragActive={activeId !== null}
                  expanded={showClosed}
                  filteredOut={items.length === 0 && (rawCounts.get(column.id) ?? 0) > 0}
                  onOpen={openDeal}
                />
              );
            })}
          </div>
          <DragOverlay dropAnimation={{ duration: 200, easing: 'ease-out' }}>
            {activeDeal ? (
              <DealCardBody
                deal={activeDeal}
                stage={stagesById.get(activeDeal.stageId)}
                now={now}
                className="ring-primary/40 scale-[1.03] rotate-[1.5deg] shadow-xl ring-1 motion-reduce:scale-100 motion-reduce:rotate-0"
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <CloseDealDialog
        open={board.closeRequest !== null}
        mode={board.closeRequest?.mode ?? 'lost'}
        deal={closingDeal ?? null}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />

      <DealDetailSheet
        nip={nip}
        dealId={openDealId}
        initial={openDealItem}
        stages={stages}
        stageIndex={stageIndex}
        now={now}
        pending={openDealId ? pending.has(openDealId) : false}
        currentUserId={currentUserId}
        currentUserName={userInfo?.name ?? ''}
        onClose={closeDeal}
        onNavigate={navigateAway}
        onDealChanged={board.applyDeal}
        onActivityLogged={board.touchActivity}
        onMove={moveDeal}
      />
    </div>
  );
}
